'use client';

import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { type PropsWithChildren, type ReactElement, type UIEventHandler, useEffect, useRef, useState } from 'react';
import { useDialog } from 'react-aria';
import { type DialogProps } from 'react-aria-components';
import { Button } from '../button/button';
import CloseIcon from '../svgs/close-icon';
import styles from './dialog.module.scss';
export type CustomDialogProps = PropsWithChildren<DialogProps & {
  className?: string;
  closeButtonLabel?: string;
  contentClassName?: string;
  footer?: ReactElement;
  footerClassName?: string;
  hasCloseButton?: boolean;
  headerAction?: ReactElement;
  headerClassName?: string;
  isLoading?: boolean;
  loader: ReactElement;
  onModalClose?: () => void;
  onScrollToBottom?: () => void;
  subtitle?: string;
  title?: string;
  titleId?: string;
}>;
export const Dialog = t0 => {
  const $ = _c(56);
  let children;
  let className;
  let closeButtonLabel;
  let contentClassName;
  let footer;
  let footerClassName;
  let headerAction;
  let headerClassName;
  let isLoading;
  let loader;
  let onModalClose;
  let onScrollToBottom;
  let props;
  let subtitle;
  let t1;
  let title;
  let titleId;
  if ($[0] !== t0) {
    ({
      children,
      className,
      closeButtonLabel,
      contentClassName,
      footer,
      footerClassName,
      hasCloseButton: t1,
      headerAction,
      headerClassName,
      isLoading,
      loader,
      onModalClose,
      onScrollToBottom,
      subtitle,
      title,
      titleId,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = closeButtonLabel;
    $[4] = contentClassName;
    $[5] = footer;
    $[6] = footerClassName;
    $[7] = headerAction;
    $[8] = headerClassName;
    $[9] = isLoading;
    $[10] = loader;
    $[11] = onModalClose;
    $[12] = onScrollToBottom;
    $[13] = props;
    $[14] = subtitle;
    $[15] = t1;
    $[16] = title;
    $[17] = titleId;
  } else {
    children = $[1];
    className = $[2];
    closeButtonLabel = $[3];
    contentClassName = $[4];
    footer = $[5];
    footerClassName = $[6];
    headerAction = $[7];
    headerClassName = $[8];
    isLoading = $[9];
    loader = $[10];
    onModalClose = $[11];
    onScrollToBottom = $[12];
    props = $[13];
    subtitle = $[14];
    t1 = $[15];
    title = $[16];
    titleId = $[17];
  }
  const hasCloseButton = t1 === undefined ? true : t1;
  const ref = useRef(null);
  const {
    dialogProps,
    titleProps
  } = useDialog(props, ref);
  const dialogContentRef = useRef(null);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  let t2;
  let t3;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const vv = window.visualViewport;
      if (!vv) {
        return;
      }
      const handleKeyboardResize = () => {
        const content = dialogContentRef.current;
        if (!content) {
          return;
        }
        const keyboardHeight = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
        if (keyboardHeight > 0) {
          content.style.paddingBottom = `${keyboardHeight}px`;
          setIsKeyboardOpen(true);
          setTimeout(() => {
            const activeElement = document.activeElement as HTMLElement | null;
            if (!activeElement || activeElement === document.body) {
              return;
            }
            const rect = activeElement.getBoundingClientRect();
            const visibleBottom = vv.offsetTop + vv.height;
            if (rect.bottom > visibleBottom) {
              content.scrollTop = content.scrollTop + (rect.bottom - visibleBottom + 16);
            }
          }, 100);
        } else {
          content.style.paddingBottom = "";
          setIsKeyboardOpen(false);
        }
      };
      vv.addEventListener("resize", handleKeyboardResize);
      return () => {
        vv.removeEventListener("resize", handleKeyboardResize);
      };
    };
    t3 = [];
    $[18] = t2;
    $[19] = t3;
  } else {
    t2 = $[18];
    t3 = $[19];
  }
  useEffect(t2, t3);
  let t4;
  if ($[20] !== footer || $[21] !== footerClassName) {
    t4 = footer && <div className={classNames(styles.dialog__footer, footerClassName)} data-testid="dialog-footer">{footer}</div>;
    $[20] = footer;
    $[21] = footerClassName;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  const footerElement = t4;
  let t5;
  if ($[23] !== onScrollToBottom) {
    t5 = event => {
      const element = event.target as HTMLDivElement;
      if (!element) {
        return;
      }
      const hasReachedBottom = Math.abs(element.scrollTop - (element.scrollHeight - element.offsetHeight)) <= 2;
      if (hasReachedBottom) {
        onScrollToBottom?.();
      }
    };
    $[23] = onScrollToBottom;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  const handleScroll = t5;
  let t6;
  let t7;
  if ($[25] !== onScrollToBottom) {
    t6 = () => {
      if (dialogContentRef.current !== null) {
        const element_0 = dialogContentRef.current;
        const hasNoVerticalScrollbar = element_0.scrollHeight <= element_0.clientHeight;
        if (hasNoVerticalScrollbar && onScrollToBottom) {
          onScrollToBottom();
        }
      }
    };
    t7 = [dialogContentRef, onScrollToBottom];
    $[25] = onScrollToBottom;
    $[26] = t6;
    $[27] = t7;
  } else {
    t6 = $[26];
    t7 = $[27];
  }
  useEffect(t6, t7);
  let t8;
  if ($[28] !== className) {
    t8 = classNames(styles.dialog, className, "flyout");
    $[28] = className;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== closeButtonLabel || $[31] !== hasCloseButton || $[32] !== headerAction || $[33] !== headerClassName || $[34] !== onModalClose || $[35] !== subtitle || $[36] !== title || $[37] !== titleId || $[38] !== titleProps) {
    t9 = title && <div className={classNames(styles.dialog__header, headerClassName)} data-testid="dialog-header"><h2 {...titleProps} className={styles.dialog__title} data-testid="dialog-header-title" id={titleId}>{title}</h2>{headerAction && headerAction}{hasCloseButton && <Button aria-label={closeButtonLabel || "close"} data-ga-exclude-tracking={true} dataTestId="dialog-header-close-button" Icon={CloseIcon} onClick={onModalClose} />}{subtitle && <p className={styles.dialog__subtitle}>{subtitle}</p>}</div>;
    $[30] = closeButtonLabel;
    $[31] = hasCloseButton;
    $[32] = headerAction;
    $[33] = headerClassName;
    $[34] = onModalClose;
    $[35] = subtitle;
    $[36] = title;
    $[37] = titleId;
    $[38] = titleProps;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  const t10 = isLoading && loader;
  let t11;
  if ($[40] !== contentClassName) {
    t11 = classNames(styles.dialog__content, contentClassName);
    $[40] = contentClassName;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  const t12 = isKeyboardOpen && footerElement;
  let t13;
  if ($[42] !== children || $[43] !== handleScroll || $[44] !== t11 || $[45] !== t12) {
    t13 = <div className={t11} data-testid="dialog-content" onScroll={handleScroll} ref={dialogContentRef}>{children}{t12}</div>;
    $[42] = children;
    $[43] = handleScroll;
    $[44] = t11;
    $[45] = t12;
    $[46] = t13;
  } else {
    t13 = $[46];
  }
  const t14 = !isKeyboardOpen && footerElement;
  let t15;
  if ($[47] !== t10 || $[48] !== t13 || $[49] !== t14) {
    t15 = <div className={styles["dialog__loading-area"]}>{t10}{t13}{t14}</div>;
    $[47] = t10;
    $[48] = t13;
    $[49] = t14;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  let t16;
  if ($[51] !== dialogProps || $[52] !== t15 || $[53] !== t8 || $[54] !== t9) {
    t16 = <div {...dialogProps} className={t8} data-testid="dialog-container" ref={ref}>{t9}{t15}</div>;
    $[51] = dialogProps;
    $[52] = t15;
    $[53] = t8;
    $[54] = t9;
    $[55] = t16;
  } else {
    t16 = $[55];
  }
  return t16;
};